import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { provinceCityData, provinceList, soilLayers } from '@/mocks/provinceData';
import { fetchSoilTemperature } from '@/utils/nasaApi';

interface LocationCardProps {
  onTsFetched: (ts: number) => void;
  externalLocation?: { province: string; city: string; nonce: number } | null;
}

export default function LocationCard({ onTsFetched, externalLocation }: LocationCardProps) {
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [soilLayer, setSoilLayer] = useState('2');
  const [fetching, setFetching] = useState(false);
  const [fetchStatus, setFetchStatus] = useState('');
  const [fetchError, setFetchError] = useState('');
  const soilLayerRef = useRef(soilLayer);

  const cities = useMemo(() => {
    if (!province || !provinceCityData[province]) return [];
    return provinceCityData[province].cities;
  }, [province]);

  const handleProvinceChange = useCallback((p: string) => {
    setProvince(p);
    setCity('');
    setLat('');
    setLon('');
    setFetchError('');
    setFetchStatus('');
  }, []);

  const handleCityChange = useCallback(
    (c: string) => {
      setCity(c);
      setFetchError('');
      if (province && provinceCityData[province]) {
        const cityInfo = provinceCityData[province].cities.find((ci) => ci.name === c);
        if (cityInfo) {
          setLat(cityInfo.lat.toFixed(4));
          setLon(cityInfo.lon.toFixed(4));
        }
      }
    },
    [province],
  );

  const runFetch = useCallback(
    async (latitude: number, longitude: number, layer: string) => {
      setFetching(true);
      setFetchError('');
      setFetchStatus('正在从 NASA POWER 获取土壤温度数据…');
      const result = await fetchSoilTemperature(latitude, longitude, layer);
      setFetching(false);
      if (result.error) {
        setFetchError(result.error);
        setFetchStatus('');
      } else {
        setFetchStatus(`成功获取土壤温度：${result.temperature.toFixed(2)} °C`);
        onTsFetched(result.temperature);
      }
    },
    [onTsFetched],
  );

  const handleFetchTs = useCallback(async () => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lon);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      setFetchError('请先输入有效的经纬度');
      return;
    }

    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      setFetchError('经纬度超出有效范围');
      return;
    }

    await runFetch(latitude, longitude, soilLayer);
  }, [lat, lon, soilLayer, runFetch]);

  useEffect(() => {
    soilLayerRef.current = soilLayer;
  }, [soilLayer]);

  useEffect(() => {
    if (!externalLocation) return;
    const provinceInfo = provinceCityData[externalLocation.province];
    if (!provinceInfo) return;
    const cityInfo = provinceInfo.cities.find((c) => c.name === externalLocation.city);
    if (!cityInfo) return;

    setProvince(externalLocation.province);
    setCity(externalLocation.city);
    setLat(cityInfo.lat.toFixed(4));
    setLon(cityInfo.lon.toFixed(4));
    setFetchError('');
    setFetchStatus('');
    runFetch(cityInfo.lat, cityInfo.lon, soilLayerRef.current);
  }, [externalLocation, runFetch]);

  return (
    <div className="bg-background-50 rounded-2xl p-5 md:p-8 border border-background-200/70 shadow-sm scroll-mt-20" id="calculator">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary-500/15 flex items-center justify-center">
          <i className="ri-map-pin-line text-primary-600 text-xl" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">输入地点</h2>
          <p className="text-sm text-foreground-600 mt-0.5">自动获取 NASA POWER 土壤温度 Ts</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground-800">
            省份
          </label>
          <select
            value={province}
            onChange={(e) => handleProvinceChange(e.target.value)}
            className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
          >
            <option value="">选择省份…</option>
            {provinceList.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground-800">
            城市
          </label>
          <select
            value={city}
            onChange={(e) => handleCityChange(e.target.value)}
            disabled={!province}
            className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <option value="">{province ? '选择城市…' : '先选省份'}</option>
            {cities.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground-800">
            纬度 Latitude
          </label>
          <input
            type="number"
            step="any"
            min={-90}
            max={90}
            placeholder="例如 52.5"
            value={lat}
            onChange={(e) => { setLat(e.target.value); setFetchError(''); }}
            className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground-800">
            经度 Longitude
          </label>
          <input
            type="number"
            step="any"
            min={-180}
            max={180}
            placeholder="例如 13.4"
            value={lon}
            onChange={(e) => { setLon(e.target.value); setFetchError(''); }}
            className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
          />
        </div>
      </div>

      <p className="text-xs text-foreground-500 mb-5">
        选省市自动填经纬度；也可直接手动输入经纬度（不限中国）
      </p>

      <div className="space-y-2 mb-5">
        <label className="block text-sm font-medium text-foreground-800">
          土壤层（NASA POWER TSOIL）
        </label>
        <select
          value={soilLayer}
          onChange={(e) => setSoilLayer(e.target.value)}
          className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
        >
          {soilLayers.map((layer) => (
            <option key={layer.value} value={layer.value}>
              {layer.label}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={handleFetchTs}
        disabled={fetching}
        className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm rounded-xl py-3.5 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {fetching ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            获取中…
          </>
        ) : (
          <>
            <i className="ri-download-cloud-line" />
            获取土壤温度（NASA POWER）
          </>
        )}
      </button>

      {fetchStatus && (
        <div className="mt-4 p-4 rounded-xl bg-primary-50 border border-primary-200 flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
          <p className="text-sm text-primary-800">{fetchStatus}</p>
        </div>
      )}

      {fetchError && (
        <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="text-sm text-red-700">{fetchError}</p>
        </div>
      )}

      <div className="mt-5 p-4 rounded-xl bg-secondary-50 border border-secondary-200/60">
        <p className="text-xs text-foreground-600 leading-relaxed">
          数据源：
          <a
            href="https://power.larc.nasa.gov/"
            target="_blank"
            rel="noreferrer"
            className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors"
          >
            NASA POWER
          </a>
          {' '}20年气候学（2001–2020）月度平均，取所选土层 TSOIL 的年均值（ANN）作为方法学所需土壤温度 Ts。默认
          <strong className="text-foreground-800"> Layer 2</strong>
          （约 10–29cm，贴近生物炭常见施用/耕层深度）。若你有实测/更权威的当地土壤温度数据，可在下方手动覆盖。
        </p>
      </div>
    </div>
  );
}